import { snapshot as authSnapshot } from "@internal/auth";
import {
  cycleId as buildCycleId,
  CYCLE_TYPE,
  type CycleData,
  type CycleRecord,
  clampTargets,
  cycleError,
} from "@/lib/cycles";
import { getDb, type PlannerDoc } from "@/lib/db";

function currentUserId(): string {
  const user = authSnapshot().user;
  if (!user) throw new Error("Not signed in");
  return user.id;
}

const writeQueues = new Map<string, Promise<unknown>>();
function enqueue<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.then(fn, fn);
  writeQueues.set(
    key,
    next.catch(() => {}),
  );
  return next;
}

/** Refused write: the cycle would share a week with another (CONTEXT.md, Cycle). */
class CycleOverlapError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CycleOverlapError";
  }
}

/**
 * Every other cycle, archived ones included. The caller's list may be stale
 * — a double click, another tab — so overlap is checked against the store.
 */
async function otherCycles(exceptId: string | null): Promise<CycleData[]> {
  const userId = currentUserId();
  const db = await getDb();
  const docs = await db.daily_planner
    .find({ selector: { user_id: userId, type: CYCLE_TYPE } })
    .exec();
  return docs
    .map((doc) => doc.toJSON() as PlannerDoc)
    .filter((json) => json.id !== exceptId)
    .map((json) => json.data as unknown as CycleData);
}

interface CreateCycleOpts {
  startYear: number;
  startWeekNumber: number;
  length: number;
  name: string;
}

export async function createCycle(opts: CreateCycleOpts): Promise<CycleRecord> {
  const userId = currentUserId();
  const data: CycleData = {
    ...opts,
    name: opts.name.trim(),
    note: "",
    archived: false,
    targets: [],
  };
  const error = cycleError(data, await otherCycles(null));
  if (error) throw new CycleOverlapError(error);
  const db = await getDb();
  const id = buildCycleId(crypto.randomUUID());
  await db.daily_planner.insert({
    id,
    type: CYCLE_TYPE,
    user_id: userId,
    data: data as unknown as Record<string, unknown>,
  });
  return { id, data };
}

/** Any edit, at any time — archived cycles included (docs/adr/0027). */
export async function updateCycle(
  id: string,
  patch: (prev: CycleData) => CycleData,
): Promise<void> {
  const db = await getDb();
  await enqueue(id, async () => {
    const doc = await db.daily_planner.findOne(id).exec();
    if (!doc) throw new Error(`Cycle ${id} not found`);
    const prev = (doc.toJSON() as PlannerDoc).data as unknown as CycleData;
    const next = clampTargets(patch(prev));
    const error = cycleError(next, await otherCycles(id));
    if (error) throw new CycleOverlapError(error);
    await doc.patch({ data: next as unknown as Record<string, unknown> });
  });
}

export async function archiveCycle(id: string): Promise<void> {
  await updateCycle(id, (prev) => ({ ...prev, archived: true }));
}

export async function reopenCycle(id: string): Promise<void> {
  await updateCycle(id, (prev) => ({ ...prev, archived: false }));
}

export async function deleteCycle(id: string): Promise<void> {
  const db = await getDb();
  const doc = await db.daily_planner.findOne(id).exec();
  if (!doc) return;
  await doc.remove();
}
